import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useFilters } from '@/context/FilterContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Trophy, TrendingUp, Zap, Target, ChevronRight, ArrowUpDown, Filter, Award, BarChart3,
} from 'lucide-react';

const API = '/api';

const SCORE_META = {
  wins: { color: '#f59e0b', label: 'Wins' },
  conversion: { color: '#10b981', label: 'Conversion' },
  deal_value: { color: '#6366f1', label: 'Deal Value' },
  velocity: { color: '#8b5cf6', label: 'Velocity' },
  activity: { color: '#f97316', label: 'Activity' },
};

const TIER_CONFIG = {
  A: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Top Tier' },
  B: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Strong' },
  C: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Average' },
  D: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', label: 'Needs Focus' },
};

function getTier(score) {
  if (score >= 70) return 'A';
  if (score >= 45) return 'B';
  if (score >= 25) return 'C';
  return 'D';
}

function MiniBar({ value, color }) {
  return (
    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color }} />
    </div>
  );
}

function ScoreBreakdown({ scores }) {
  return (
    <div className="space-y-1.5 w-32">
      {Object.entries(SCORE_META).map(([key, m]) => (
        <TooltipProvider key={key}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 w-[50px] truncate">{m.label}</span>
                <div className="flex-1"><MiniBar value={scores[key]} color={m.color} /></div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top"><p>{m.label}: {scores[key]}/100</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
}

function PodiumCard({ dealer, rank, navigate }) {
  const isFirst = rank === 1;
  const tierCfg = TIER_CONFIG[getTier(dealer.scores.composite)];

  return (
    <Card
      data-testid={`podium-card-${rank}`}
      className={`cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
        isFirst ? 'ring-1 ring-amber-200 shadow-md' : ''
      }`}
      onClick={() => navigate(`/profile/dealer/${encodeURIComponent(dealer.name)}`)}
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={`flex items-center justify-center w-9 h-9 rounded-full ${
            rank === 1 ? 'bg-amber-100 text-amber-600' :
            rank === 2 ? 'bg-slate-100 text-slate-500' :
            'bg-orange-50 text-orange-500'
          }`}>
            {rank === 1 ? <Trophy className="h-4 w-4" /> : <Award className="h-4 w-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-400">#{rank}</span>
              {isFirst && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-50 text-amber-600 border-amber-200">Leader</Badge>}
            </div>
            <p className="font-semibold text-sm truncate mt-0.5" title={dealer.name}>{dealer.name}</p>
          </div>
        </div>

        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-bold tracking-tight">{dealer.scores.composite}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Composite Score</p>
          </div>
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${tierCfg.bg} ${tierCfg.text} ${tierCfg.border}`}>
            {tierCfg.label}
          </Badge>
        </div>

        <div className="border-t mt-3 pt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-sm font-semibold">{dealer.won_leads}</p>
            <p className="text-[10px] text-slate-400">Won</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-600">{dealer.conversion_rate}%</p>
            <p className="text-[10px] text-slate-400">Conv</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-indigo-600">{dealer.avg_deal_kva}</p>
            <p className="text-[10px] text-slate-400">Avg KVA</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{label}</p>
          <p className="text-xl font-bold tracking-tight mt-0.5">
            {value}{sub && <span className="text-xs text-slate-400 font-normal ml-0.5">{sub}</span>}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DealerScorecard() {
  const { buildQueryParams } = useFilters();
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minLeads, setMinLeads] = useState('5');
  const [sortBy, setSortBy] = useState('composite');
  const [weights, setWeights] = useState({});
  const [dateRange, setDateRange] = useState({});
  const [stateFilter, setStateFilter] = useState('all');
  const [states, setStates] = useState([]);

  const loadScorecard = useCallback(async () => {
    setLoading(true);
    try {
      const qp = buildQueryParams();
      let url = `${API}/insights/dealer-scorecard?min_leads=${minLeads}&${qp}`;
      if (stateFilter !== 'all') url += `&state=${encodeURIComponent(stateFilter)}`;
      const res = await axios.get(url, { withCredentials: true });
      setDealers(res.data.dealers || []);
      setWeights(res.data.weights || {});
      setDateRange(res.data.date_range || {});
    } catch (err) {
      console.error('Scorecard load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, minLeads, stateFilter]);

  useEffect(() => { loadScorecard(); }, [loadScorecard]);
  useEffect(() => {
    setStates([...new Set(dealers.map(d => d.state).filter(Boolean))].sort());
  }, [dealers]);

  const sorted = [...dealers].sort((a, b) => {
    if (sortBy === 'composite') return b.scores.composite - a.scores.composite;
    if (sortBy === 'wins') return b.won_leads - a.won_leads;
    if (sortBy === 'conversion') return b.conversion_rate - a.conversion_rate;
    if (sortBy === 'deal_value') return b.avg_deal_kva - a.avg_deal_kva;
    if (sortBy === 'velocity') return (a.avg_closure_days || 999) - (b.avg_closure_days || 999);
    if (sortBy === 'activity') return b.activity_score_raw - a.activity_score_raw;
    return 0;
  });

  const top3 = dealers.slice(0, 3);
  const avgComposite = dealers.length ? (dealers.reduce((s, d) => s + d.scores.composite, 0) / dealers.length).toFixed(1) : 0;
  const totalWon = dealers.reduce((s, d) => s + d.won_leads, 0);
  const avgConv = dealers.length ? (dealers.reduce((s, d) => s + d.conversion_rate, 0) / dealers.length).toFixed(1) : 0;

  if (loading) return (
    <div className="space-y-5" data-testid="dealer-scorecard-loading">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      <div className="grid grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-44" />)}</div>
      <Skeleton className="h-96" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dealer-scorecard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-500" />
            Dealer Scorecard
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Composite performance ranking across {dealers.length} dealers
            {dateRange.start_date && <span className="text-slate-300 ml-1">({dateRange.start_date} to {dateRange.end_date})</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={minLeads} onValueChange={setMinLeads}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="min-leads-filter">
              <Filter className="h-3 w-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Min 3 leads</SelectItem>
              <SelectItem value="5">Min 5 leads</SelectItem>
              <SelectItem value="10">Min 10 leads</SelectItem>
              <SelectItem value="20">Min 20 leads</SelectItem>
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="state-filter"><SelectValue placeholder="All States" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="sort-filter">
              <ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="composite">Composite Score</SelectItem>
              <SelectItem value="wins">Win Count</SelectItem>
              <SelectItem value="conversion">Conversion Rate</SelectItem>
              <SelectItem value="deal_value">Avg Deal KVA</SelectItem>
              <SelectItem value="velocity">Sales Velocity</SelectItem>
              <SelectItem value="activity">Activity Score</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Target} label="Avg Score" value={avgComposite} sub="/100" color="bg-amber-50 text-amber-600" />
        <StatCard icon={Trophy} label="Total Won" value={totalWon} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={TrendingUp} label="Avg Conv%" value={`${avgConv}%`} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Zap} label="Dealers Ranked" value={dealers.length} color="bg-violet-50 text-violet-600" />
      </div>

      {/* Top 3 Podium */}
      {top3.length >= 3 && (
        <div data-testid="podium-section">
          <h2 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5 text-amber-500" /> Top Performers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {top3.map((d, i) => (
              <PodiumCard key={d.name} dealer={d} rank={i + 1} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* Score Weights Legend */}
      <Card>
        <CardContent className="py-2.5 px-4">
          <div className="flex flex-wrap items-center gap-5 text-xs">
            <span className="text-slate-400 font-medium uppercase tracking-wider">Score Weights</span>
            {Object.entries(SCORE_META).map(([key, m]) => (
              <span key={key} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: m.color }} />
                <span className="text-slate-500">{m.label}</span>
                <span className="font-semibold text-slate-700">{((weights[key] || 0) * 100).toFixed(0)}%</span>
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Full Leaderboard */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Full Leaderboard</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {sorted.length === 0 ? (
            <div className="py-12 text-center text-slate-400">No dealers match the current criteria.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="scorecard-table">
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="w-10 text-[11px]">#</TableHead>
                    <TableHead className="text-[11px]">Dealer</TableHead>
                    <TableHead className="text-center text-[11px]">Score</TableHead>
                    <TableHead className="hidden lg:table-cell text-[11px]">Breakdown</TableHead>
                    <TableHead className="text-center text-[11px]">Won</TableHead>
                    <TableHead className="text-center text-[11px]">Conv%</TableHead>
                    <TableHead className="text-center hidden md:table-cell text-[11px]">Avg KVA</TableHead>
                    <TableHead className="text-center hidden md:table-cell text-[11px]">Closure</TableHead>
                    <TableHead className="text-center hidden xl:table-cell text-[11px]">Activity</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(d => {
                    const tier = getTier(d.scores.composite);
                    const tierCfg = TIER_CONFIG[tier];
                    return (
                      <TableRow
                        key={d.name}
                        data-testid={`dealer-row-${d.rank}`}
                        className="cursor-pointer hover:bg-slate-50/80 transition-colors"
                        onClick={() => navigate(`/profile/dealer/${encodeURIComponent(d.name)}`)}
                      >
                        <TableCell className="font-mono text-xs text-slate-400">{d.rank}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{d.name}</span>
                            {d.rank <= 3 && <Trophy className="h-3 w-3 text-amber-400 shrink-0" />}
                          </div>
                          {d.state && <span className="text-[10px] text-slate-400">{d.state}</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="inline-flex items-center gap-1.5">
                            <span className="text-lg font-bold">{d.scores.composite}</span>
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${tierCfg.bg} ${tierCfg.text} ${tierCfg.border}`}>{tier}</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <ScoreBreakdown scores={d.scores} />
                        </TableCell>
                        <TableCell className="text-center font-medium text-sm">{d.won_leads}</TableCell>
                        <TableCell className="text-center text-sm text-emerald-600 font-medium">{d.conversion_rate}%</TableCell>
                        <TableCell className="text-center text-sm text-indigo-600 hidden md:table-cell">{d.avg_deal_kva}</TableCell>
                        <TableCell className="text-center text-sm hidden md:table-cell text-slate-600">
                          {d.avg_closure_days != null ? `${d.avg_closure_days}d` : <span className="text-slate-300">-</span>}
                        </TableCell>
                        <TableCell className="text-center text-sm hidden xl:table-cell text-slate-600">{d.activity_score_raw}</TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-slate-300" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
